import { useCallback, type PropsWithChildren } from "react";
import type { IntercomAuthenticationSettings, IntercomThemeMode } from "../hooks/useIntercomWidget";
import useIntercomWidget from "../hooks/useIntercomWidget";
import { getSharedVariables } from "../variables";
import { IntercomContext } from "./IntercomContext";

const HIDE_STYLE_ID = "intercom-launcher-hidden";

interface Props {
  fullName: string;
  email: string;
  settings?: IntercomAuthenticationSettings;
  themeMode?: IntercomThemeMode;
}

const setHideEffect = (shouldHide: boolean) => {
  const existing = document.getElementById(HIDE_STYLE_ID);
  if (shouldHide) {
    if (existing) return;
    const style = document.createElement("style");
    style.id = HIDE_STYLE_ID;
    style.textContent = `
      .intercom-lightweight-app-launcher,
      #intercom-container { display: none !important; }
    `;
    document.head.appendChild(style);
  } else {
    existing?.remove();
  }
};

const setOpacityEffect = (shouldDim: boolean) => {
  const element =
    document.querySelector<HTMLElement>(".intercom-launcher-frame")?.parentElement ??
    document.querySelector<HTMLElement>(".intercom-lightweight-app-launcher");
  if (element) {
    element.style.opacity = shouldDim ? "0.2" : "1";
  }
};

const changeEffect = (effect: "opacity" | "hide", shouldApplyEffect: boolean) => {
  if (effect === "hide") {
    setHideEffect(shouldApplyEffect);
  } else {
    setOpacityEffect(shouldApplyEffect);
  }
};

const IntercomContextProvider = ({ fullName, email, settings, themeMode, children }: PropsWithChildren<Props>) => {
  const { isIntercomWidgetAvailable } = useIntercomWidget({ fullName, email, settings, themeMode });

  const getArticleUrl = useCallback(
    (articleId: string) => new URL(`en/articles/${articleId}`, getSharedVariables().HELP_CENTER_URI).href,
    []
  );

  const showArticle = useCallback(
    (articleId: string) => {
      if (typeof window.Intercom === "function") {
        window.Intercom("showArticle", articleId);
        return;
      }
      window.open(getArticleUrl(articleId), "_blank", "noopener,noreferrer");
    },
    [getArticleUrl]
  );

  const showNewMessage = useCallback((message?: string) => {
    if (typeof window.Intercom === "function") {
      window.Intercom("showNewMessage", message);
    }
  }, []);

  const addEffect = useCallback((effect: "opacity" | "hide") => {
    changeEffect(effect, true);
  }, []);

  const removeEffect = useCallback((effect: "opacity" | "hide") => {
    changeEffect(effect, false);
  }, []);

  return (
    <IntercomContext
      value={{
        isIntercomWidgetLoaded: isIntercomWidgetAvailable,
        getArticleUrl,
        showArticle,
        showNewMessage,
        addEffect,
        removeEffect,
      }}
    >
      {children}
    </IntercomContext>
  );
};

export default IntercomContextProvider;
